import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

const submissionResultSchema = z.object({
  draftSalesOrderId: z.string().uuid(),
  salesOrderId: z.string().uuid().nullable(),
  success: z.boolean(),
  error: z.string().optional(),
});

/**
 * Submit Draft Sales Orders Step (workflow-native approval flow).
 *
 * Relational counterpart to the staged submit step. Creates real sales orders
 * from APPROVED draft sales orders (reading relational lines) and syncs them to
 * the ERP. Runs after the upstream team Approval steps have gated approval, so
 * it performs no gate re-validation and has no auto-submit toggle — reaching
 * this step IS the go-ahead. Idempotent per draft.
 *
 * Backend-only (DB + ERP access); handler in
 * backend/src/workflows/steps/order-management/.
 */
export const submitDraftSalesOrdersStep = defineStepConfig({
  type: "submit_draft_sales_orders",
  name: "Submit Draft Sales Orders",
  description:
    "Creates real sales orders from approved draft sales orders and syncs them to the ERP",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    draftSalesOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the approved draft sales orders to submit"),
  }),

  outputSchema: z.object({
    results: z
      .array(submissionResultSchema)
      .describe("Per-draft submission result"),
    salesOrderIds: z
      .array(z.string().uuid())
      .describe("Ids of the sales orders created (or already existing)"),
    successCount: z.number().int(),
    failureCount: z.number().int(),
  }),

  configSchema: z.object({
    continueOnError: z
      .boolean()
      .default(true)
      .describe(
        "When true, a failed submission is recorded and the remaining drafts still process; when false, the step stops at the first failure",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
